import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    getAuthToken,
    getSessionTimeLeftMs,
    getTokenLifetimeMs,
    hasActiveSession,
    isPublicPath,
    subscribeToSessionChanges,
} from "@/utility/authSession";
import { renewAuthToken } from "@/redux/Auth/actions";

/** Longest heads-up we give before the token expires. */
const MAX_WARN_BEFORE_MS = 5 * 60 * 1000;
/** Keep the warning useful even for very short-lived tokens. */
const MIN_WARN_BEFORE_MS = 60 * 1000;
/**
 * No pointer/keyboard/scroll in this window = inactive.
 * Only then may the expiry message appear.
 */
const IDLE_MS = 10 * 60 * 1000;

const ACTIVITY_EVENTS = [
    "mousedown",
    "mousemove",
    "keydown",
    "scroll",
    "touchstart",
    "wheel",
    "pointerdown",
];

/**
 * Warn 5 minutes before a 180-minute token expires. Shorter tokens get a
 * smaller window so the warning does not fire right after login.
 */
const resolveWarnBeforeMs = () => {
    const lifetime = getTokenLifetimeMs(getAuthToken());
    if (lifetime == null) return MAX_WARN_BEFORE_MS;
    return Math.min(
        MAX_WARN_BEFORE_MS,
        Math.max(MIN_WARN_BEFORE_MS, Math.round(lifetime / 4)),
    );
};

const formatCountdown = (ms) => {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

const onPublicPath = () => isPublicPath(window.location.pathname);

const isTabVisible = () =>
    typeof document === "undefined" || document.visibilityState === "visible";

/**
 * Visible tab + recent input (or page just opened) counts as an active user.
 * A hidden tab, or a visible tab left untouched for IDLE_MS, is inactive.
 */
const isUserActive = (lastActivityAt) =>
    isTabVisible() && Date.now() - lastActivityAt < IDLE_MS;

/**
 * Active users get a silent token refresh. The expiry card only appears after
 * the user has been idle on this page for IDLE_MS and the token is near expiry.
 */
const SessionExpiryNotice = () => {
    const { pathname } = useLocation();
    const [endedReason, setEndedReason] = useState(null);
    /** Milliseconds left, set only while the idle warning is on screen. */
    const [msLeft, setMsLeft] = useState(null);
    const [extending, setExtending] = useState(false);
    const hadSessionRef = useRef(false);
    const evaluateRef = useRef(() => {});
    const lastActivityAtRef = useRef(Date.now());
    const renewingRef = useRef(false);
    const warningVisibleRef = useRef(false);

    useEffect(() => {
        warningVisibleRef.current = msLeft != null;
    }, [msLeft]);

    useEffect(() => {
        let warnTimer;
        let expiryTimer;
        let countdown;
        let idleTimer;

        const clearTimers = () => {
            clearTimeout(warnTimer);
            clearTimeout(expiryTimer);
            clearInterval(countdown);
        };

        const armIdleTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => evaluateRef.current("idle"), IDLE_MS);
        };

        const markActivity = () => {
            const now = Date.now();
            if (now - lastActivityAtRef.current < 1000) return;
            lastActivityAtRef.current = now;
            armIdleTimer();
        };

        const trySilentRenew = async () => {
            if (renewingRef.current) return false;
            renewingRef.current = true;
            try {
                await renewAuthToken();
                evaluateRef.current("renewed");
                return true;
            } catch {
                return false;
            } finally {
                renewingRef.current = false;
            }
        };

        const startCountdown = (timeLeft) => {
            setMsLeft(Math.max(0, timeLeft));
            countdown = setInterval(() => {
                const left = getSessionTimeLeftMs();
                setMsLeft(left == null ? null : Math.max(0, left));
            }, 1000);
        };

        const evaluate = (reason) => {
            clearTimers();

            if (hasActiveSession()) {
                hadSessionRef.current = true;
                setEndedReason(null);

                const timeLeft = getSessionTimeLeftMs();
                if (timeLeft == null) {
                    setMsLeft(null);
                    return;
                }

                const warnBefore = resolveWarnBeforeMs();
                const active = isUserActive(lastActivityAtRef.current);

                if (timeLeft > warnBefore) {
                    setMsLeft(null);
                    warnTimer = setTimeout(() => evaluate("warn"), timeLeft - warnBefore);
                    expiryTimer = setTimeout(() => evaluate("expired"), Math.max(timeLeft, 0) + 250);
                    return;
                }

                // Token is in the warning window.
                if (active && !warningVisibleRef.current) {
                    setMsLeft(null);
                    void trySilentRenew().then((ok) => {
                        if (ok) return;
                        if (!hasActiveSession()) {
                            evaluate("expired");
                            return;
                        }
                        startCountdown(getSessionTimeLeftMs() ?? 0);
                    });
                    expiryTimer = setTimeout(() => evaluate("expired"), Math.max(timeLeft, 0) + 250);
                    return;
                }

                startCountdown(timeLeft);
                expiryTimer = setTimeout(() => evaluate("expired"), Math.max(timeLeft, 0) + 250);
                return;
            }

            setMsLeft(null);
            if (hadSessionRef.current && !onPublicPath()) {
                setEndedReason(reason === "changed" ? "expired" : reason);
            }
        };

        evaluateRef.current = evaluate;
        lastActivityAtRef.current = Date.now();
        armIdleTimer();
        evaluate("init");
        const unsubscribe = subscribeToSessionChanges(evaluate);

        const activityOptions = { passive: true, capture: true };
        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, markActivity, activityOptions);
        });
        const onVisibility = () => {
            if (document.visibilityState === "visible") markActivity();
            else evaluate("hidden");
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            clearTimers();
            clearTimeout(idleTimer);
            unsubscribe();
            ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, markActivity, activityOptions);
            });
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const extendSession = useCallback(async () => {
        setExtending(true);
        try {
            await renewAuthToken();
            lastActivityAtRef.current = Date.now();
            evaluateRef.current("renewed");
            toast.success("Session extended — you can keep working.");
        } catch (_) {
            toast.error("Could not extend the session. Save your work and sign in again.");
        } finally {
            setExtending(false);
        }
    }, []);

    const onPublicScreen = isPublicPath(pathname);

    if (!endedReason && msLeft != null && !onPublicScreen) {
        return (
            <div
                role="alertdialog"
                aria-labelledby="session-warning-title"
                className="fixed bottom-5 right-5 z-[9999] w-[22rem] max-w-[calc(100vw-2.5rem)] rounded-xl border border-amber-300 bg-white p-4 shadow-2xl dark:border-amber-500/40 dark:bg-slate-900"
            >
                <h2
                    id="session-warning-title"
                    className="text-sm font-semibold text-slate-900 dark:text-slate-100"
                >
                    Your session ends in {formatCountdown(msLeft)}
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    You have been inactive. Extend the session now to stay signed in.
                    If the timer runs out you will be signed out and any unsaved work
                    on this page will be lost.
                </p>
                <button
                    type="button"
                    onClick={extendSession}
                    disabled={extending}
                    className="mt-3.5 w-full rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                    {extending ? "Extending…" : "Stay signed in"}
                </button>
            </div>
        );
    }

    if (!endedReason || onPublicScreen) return null;

    const NOTICES = {
        logout: {
            title: "You have been signed out",
            message:
                "This account was signed out in another tab. Please refresh the page and sign in again before continuing.",
        },
        "signed-out": {
            title: "You have been signed out",
            message:
                "This account was signed in on another device, so this session ended. Please refresh the page and sign in again before continuing.",
        },
        unauthorized: {
            title: "Your session is no longer valid",
            message:
                "The server rejected this session. Please refresh the page and sign in again before starting work.",
        },
    };

    const { title, message } = NOTICES[endedReason] ?? {
        title: "Your session has expired",
        message:
            "This tab was idle and the session timed out. Please refresh the page and sign in again before starting work.",
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="session-expiry-title"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm"
        >
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-900">
                <h2
                    id="session-expiry-title"
                    className="text-lg font-semibold text-slate-900 dark:text-slate-100"
                >
                    {title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    {message}
                </p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                >
                    Refresh page
                </button>
            </div>
        </div>
    );
};

export default SessionExpiryNotice;
